import { EyeOutlined } from '@ant-design/icons';
import type { DeliveryProvider, Order, PaymentMethod } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button, Tabs, Tag } from 'antd';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { BottomPagination } from '../../components/BottomPagination';
import { ORDER_STATUS_OPTIONS, OrderStatusTag } from '../../components/OrderStatusTag';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StyledTable,
  TableContainer,
  Toolbar,
} from '../../components/PageLayout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useListNavigate } from '../../hooks/useListReturn';
import { useOrders } from '../../hooks/useOrderQueries';
import { usePagination } from '../../hooks/usePagination';
import { useTableSorter } from '../../hooks/useTableSorter';
import { useBooleanParams, useStringParams } from '../../hooks/useUrlParam';
import { ROUTES } from '../../routes/routes';
import { formatPrice } from '../../utils/currency';

import { OrderFilters } from './OrderFilters';

const OrderCard = styled.div`
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OrderCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const OrderCardMeta = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  cash_on_delivery: 'Накладений платіж',
  card_transfer: 'На картку',
};

const DELIVERY_LABELS: Record<DeliveryProvider, string> = {
  nova_poshta: 'Нова Пошта',
  ukrposhta: 'Укрпошта',
};

const tabItems = [
  { key: 'all', label: 'Всі' },
  ...ORDER_STATUS_OPTIONS.map(({ value, label }) => ({ key: value, label })),
];

export const OrdersPage: React.FC = () => {
  const goToDetail = useListNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize } = usePagination();

  const booleanFilters = useBooleanParams(['isPaid', 'isCashed']);
  const stringFilters = useStringParams(['status', 'deliveryProvider']);

  const filters = useMemo(
    () => ({ ...booleanFilters, ...stringFilters }),
    [booleanFilters, stringFilters],
  );

  const { sorter, handleTableChange } = useTableSorter();
  const isMobile = useIsMobile();

  const { data: paginatedOrders, isLoading } = useOrders({
    page,
    limit: pageSize,
    filters,
    sorter,
  });
  const orders = paginatedOrders?.hits || [];
  const totalOrders = paginatedOrders?.total || 0;

  const activeTab = searchParams.get('status') ?? 'all';

  const handleTabChange = (key: string) => {
    setSearchParams((prev) => {
      if (key === 'all') prev.delete('status');
      else prev.set('status', key);
      return prev;
    });
  };

  const handleViewOrder = (id: number) => {
    goToDetail(ROUTES.ORDER_VIEW.replace(':id', String(id)));
  };

  const columns: TableProps<Order>['columns'] = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: true,
      render: (id: number) => String(id).padStart(4, '0'),
    },
    {
      title: 'Отримувач',
      key: 'recipient',
      render: (_, record) => `${record.recipient.lastName} ${record.recipient.firstName}`,
    },
    {
      title: 'Телефон',
      key: 'phone',
      render: (_, record) => record.recipient.phone,
    },
    {
      title: 'Сума',
      dataIndex: 'total',
      key: 'total',
      sorter: true,
      render: (total: number) => formatPrice(total),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: Order['status']) => <OrderStatusTag status={status} />,
    },
    {
      title: 'Оплата',
      key: 'payment',
      render: (_, record) => (
        <>
          {PAYMENT_LABELS[record.paymentMethod]}
          {' — '}
          <Tag color={record.isPaid ? 'green' : 'default'}>
            {record.isPaid ? 'Оплачено' : 'Не оплачено'}
          </Tag>
        </>
      ),
    },
    {
      title: 'Каса',
      key: 'isCashed',
      render: (_, record) =>
        record.delivery.provider === 'nova_poshta' ? (
          <Tag color={record.isCashed ? 'green' : 'default'}>{record.isCashed ? 'Так' : 'Ні'}</Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Доставка',
      key: 'delivery',
      render: (_, record) => DELIVERY_LABELS[record.delivery.provider],
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) =>
        new Date(date).toLocaleDateString('uk-UA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
    {
      title: 'Дія',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Button icon={<EyeOutlined />} onClick={() => handleViewOrder(record.id)} />
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Замовлення</PageTitle>
      </PageHeader>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{ marginBottom: 0 }}
      />
      <Toolbar style={{ marginTop: 16 }}>
        <OrderFilters />
      </Toolbar>
      {isMobile ? (
        <>
          {isLoading
            ? null
            : orders.map((order) => (
                <OrderCard key={order.id}>
                  <OrderCardRow>
                    <span style={{ fontWeight: 700 }}>№{String(order.id).padStart(4, '0')}</span>
                    <OrderStatusTag status={order.status} />
                  </OrderCardRow>
                  <OrderCardMeta>
                    {order.recipient.lastName} {order.recipient.firstName} · {order.recipient.phone}
                  </OrderCardMeta>
                  <OrderCardRow>
                    <OrderCardMeta>
                      {formatPrice(order.total)} · {DELIVERY_LABELS[order.delivery.provider]}
                    </OrderCardMeta>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewOrder(order.id)}
                    />
                  </OrderCardRow>
                </OrderCard>
              ))}
        </>
      ) : (
        <TableContainer>
          <StyledTable
            columns={columns}
            dataSource={orders}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
          />
        </TableContainer>
      )}
      <BottomPagination total={totalOrders} />
    </PageContainer>
  );
};
